'use client';
// import dynamic from 'next/dynamic'

import { z } from 'zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as R from 'remeda';
import { toDateTime } from '@repo/common/utils';
import { ImmerHook, Updater, useImmer } from 'use-immer';
import { useQuery, useTRPC } from '@/api/trpc';
import { useGlobalAccountContext } from '@/components/AccountPicker';
import { EXTENSION_ORIGIN } from '@repo/common/config';

// const noSsr = <T extends ComponentType>(component: T) => dynamic(async () => component, {ssr: false});

// List of localStorage keys we use (so they can be cleared on signout)
export const LocalStorageKey = z.enum([
  'opportunity-list-filters',
  'opportunity-selected-sort',
  'ask-toro-extension-state',
  'toro-extension-settings',
]);
export type LocalStorageKey = z.infer<typeof LocalStorageKey>;

export type LocalStorageHook<T> = [...ImmerHook<T>, { isLoading: boolean }];

export const useLocalStorageState = <S extends z.ZodType>(
  localStorageKey: LocalStorageKey,
  schema: S,
  initialValue: z.infer<S>
): LocalStorageHook<z.infer<S>> => {
  'use client';
  type T = z.infer<S>;
  const parseFromStorage = useCallback((): T => {
    try {
      const loadedFilters = localStorage.getItem(localStorageKey);
      if (!loadedFilters) {
        return initialValue;
      }
      try {
        const parsedJson = JSON.parse(loadedFilters);

        // Handle date conversion for dateFilter if it exists
        if (parsedJson.dateFilter) {
          const { type } = parsedJson.dateFilter;

          if (['exact', 'before', 'after'].includes(type)) {
            // Convert date string back to Date object u
            if (parsedJson.dateFilter.date) {
              const dateTime = toDateTime(parsedJson.dateFilter.date);
              parsedJson.dateFilter.date = dateTime.toJSDate();
            }
          } else if (type === 'range') {
            if (parsedJson.dateFilter.startDate) {
              const startDateTime = toDateTime(parsedJson.dateFilter.startDate);
              parsedJson.dateFilter.startDate = startDateTime.toJSDate();
            }
            if (parsedJson.dateFilter.endDate) {
              const endDateTime = toDateTime(parsedJson.dateFilter.endDate);
              parsedJson.dateFilter.endDate = endDateTime.toJSDate();
            }
          }
        }

        return schema.parse(parsedJson);
      } catch (err) {
        console.error('Error parsing value from localStorage', err);
        localStorage.removeItem(localStorageKey);
      }
    } catch (err) {
      console.warn('Error accessing localStorage');
    }
    return initialValue;
  }, [initialValue, localStorageKey, schema]);

  const [state, setState] = useImmer<T>(initialValue);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isLoading) return;

    const parsedValue = parseFromStorage();
    if (R.isDeepEqual(state, parsedValue)) {
      console.log('Loaded value matches current value. Skipping.', { state, parsedValue });
    } else {
      console.log('Restoring saved value', parsedValue);
      setState(parsedValue);
    }
    setIsLoading(false);
  }, [parseFromStorage, state, setState, isLoading]);

  // Wrapper to set value and save to localStorage
  const setValue = useCallback<Updater<T>>(
    (setter) => {
      setState((draft) => {
        const result = R.isFunction(setter) ? setter(draft) : setter;

        // If setter returned a value, use that, otherwise draft was mutated
        const finalValue = result !== undefined ? result : (draft as T);
        localStorage.setItem(localStorageKey, JSON.stringify(finalValue));

        return result;
      });
    },
    [localStorageKey, setState]
  );

  return [state, setValue, { isLoading }];
};

// Clears local storage keys used by our code
export function clearLocalStorage() {
  // Only try clearing localStorage client side
  if (typeof window !== 'undefined') {
    console.log('Clearing all local storage keys');
    LocalStorageKey.options.forEach((key) => {
      localStorage.removeItem(key);
    });
  }
}

/**
 * Returns the full current user from the database
 */
export function useCurrentUser() {
  const trpc = useTRPC();
  return useQuery(trpc.users.me.queryOptions());
}

/**
 * Syncs user session info to parent window (SidePanel)
 * so it can be stored in chrome.storage for the Options page
 * TODO: this may no longer be necessary since the extension can just hit this endpoint directly
 */
export function useSyncUserSessionToParent() {
  const { data: me, isLoading } = useCurrentUser();

  const userSessionInfo = useMemo(() => {
    if (isLoading || !me) return null;
    return R.pick(me, ['name', 'email', 'accountName']);
  }, [me, isLoading]);

  useEffect(() => {
    // Only sync once finished loading
    if (!userSessionInfo) return;

    console.log('Sending user session info to parent', userSessionInfo);
    window.parent.postMessage(
      {
        type: 'UPDATE_USER_SESSION_INFO',
        userSessionInfo,
      },
      EXTENSION_ORIGIN
    );
  }, [userSessionInfo]);
}
